/* 
 * Copyright (C) 2022 SHC Holdings LLC.  All Rights Reserved. 
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { Layer, Source } from 'react-map-gl';

import { exportFilterSettingPayload, GeoLevelEnum, useAPI, useFilterSetting, ViewModeEnum, MapFallback } from 'nmqf-common-ui';
import geojson from './regions.json';
import { ILLINOIS_REGIONS } from '../../filterbar';

const REGION_STATISTIC_DATA = '/api/stats/calculate-statistic-region-overlay';

const regionLayer = {
  id: 'region-layer',
  type: 'line',
  paint: {
    'line-color': '#007FFF',
    'line-width': 2,
  },
};

interface IllinoisRegionLayerProps {
  regions?: string[];
}

export const IllinoisRegionLayer: React.FC<IllinoisRegionLayerProps> = React.memo((props) => {
  const [regionData, fetchRegionData, clearData] = useAPI(REGION_STATISTIC_DATA, undefined, 'POST');
  const filterSetting = useFilterSetting();
  const forceUpdateRef = useRef<number>();
  const isShowingRegions = !!props.regions?.length;

  const fetchDataWrapped = () => {
    const payload = exportFilterSettingPayload(filterSetting);
    fetchRegionData({
      ...payload,
      displayOption: {
        displayOptionType: ViewModeEnum.Map,
        mapView: { type: GeoLevelEnum.Zip5 },
      },
      regionOverlayParam: {
        regionNames: ILLINOIS_REGIONS,
        stateAbb: 'IL',
        stateCode: 17,
      },
    });
  };

  useEffect(() => {
    if (isShowingRegions && forceUpdateRef.current !== filterSetting.forceUpdate) {
      clearData();
      fetchDataWrapped();
      forceUpdateRef.current = filterSetting.forceUpdate;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSetting.forceUpdate, isShowingRegions]);

  const geojsonData: any = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: geojson.features.filter((f) => props.regions?.some((v) => v === f.properties.Region)),
    }),
    [props.regions]
  );

  if(!isShowingRegions) return null;
  return (
    <>
      {regionData.data && (
        <Source type="geojson" data={geojsonData}>
          <Layer {...regionLayer} />
        </Source>
      )}
      <MapFallback requesting={regionData.requesting} error={regionData.error} retry={fetchDataWrapped} />
    </>
  );
});
